import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ApiService from '../services/api';
import { Article, Period, Category } from '../types';

const Home: React.FC = () => {
  const [featuredArticles, setFeaturedArticles] = useState<Article[]>([]);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [articlesData, periodsData, categoriesData] = await Promise.all([
          ApiService.getArticles({ featured: true, limit: 12 }),
          ApiService.getPeriods(),
          ApiService.getCategories()
        ]);
        
        setFeaturedArticles(articlesData);
        setPeriods(periodsData);
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <div className="flex justify-center items-center min-h-screen">Loading...</div>;
  }

  return (
    <>
      {/* Hero Section */}
      <section 
        style={{
          position: 'relative',
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: '2rem',
          backgroundImage: 'linear-gradient(rgba(33, 30, 26, 0.7) 0%, rgba(33, 30, 26, 0.9) 100%), url("https://lh3.googleusercontent.com/aida-public/AB6AXuCdjYWMK-KDjuM5OabM4s2RqWU2nCqooIJ1U-9brPoxw5YY7vl5KaWVHoFXC7aemr6SjqNE2PX4UFZphj6Jv8Frt8m2NvbokOxe-AvLLmEd4qFpVd0WzZH_1iWSdyUCypAfrxCni0sBnAtJicWmotC6mor3jfCQiZ6W9HwnQDpsMkpMBrPZpUdH3sykGa7D3APCRc8MeYuVAk0lJOYdajFg1YaIQInvG7QV5rm1CbuDyFaj460TGokJDA2YPvcbmxexCwD5X45Z_K3P")',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div style={{ maxWidth: '48rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem' }}>
          <h1 style={{ 
            fontSize: '3rem', 
            fontWeight: '900', 
            textShadow: '0 0 8px rgba(192, 160, 128, 0.5)',
            letterSpacing: '0.025em',
            color: '#f5f0e8',
            textAlign: 'center',
            lineHeight: '1.1',
            margin: '0'
          }}>
            History is more than a list of famous figures and dates.
          </h1>
          <p style={{
            fontSize: '1.125rem',
            color: 'rgba(245, 240, 232, 0.9)',
            textAlign: 'center',
            lineHeight: '1.6',
            margin: '0'
          }}>
            Join us on a digital expedition into the archives, uncovering the fascinating, overlooked moments from local and forgotten histories. From small-town secrets to everyday historical tasks, we're here to show you that every story matters.
          </p>
          <Link 
            to="/search"
            className="skeuomorphic-btn"
            style={{
              display: 'inline-block',
              padding: '0.75rem 2rem',
              fontSize: '1.125rem',
              fontWeight: 'bold',
              color: 'black',
              borderRadius: '9999px',
              textDecoration: 'none',
              transition: 'transform 0.3s ease',
              marginTop: '1rem'
            }}
          >
            Explore Some History
          </Link>
        </div>
        <div style={{
          position: 'absolute',
          bottom: '0',
          left: '0',
          width: '100%',
          height: '6rem',
          background: 'linear-gradient(to top, var(--background-color), transparent)'
        }}></div>
      </section>

      <div style={{ 
        paddingLeft: '1rem', 
        paddingRight: '1rem',
        paddingTop: '4rem', 
        paddingBottom: '4rem' 
      }}>
        {/* Featured Articles */}
        <section style={{ marginBottom: '5rem' }}>
          <h2 style={{
            fontSize: '2.25rem',
            fontWeight: 'bold',
            marginBottom: '2rem',
            textAlign: 'center',
            color: '#c0a080',
            letterSpacing: '0.05em',
            fontFamily: 'Merriweather, serif'
          }}>
            Featured Articles
          </h2>
          <div style={{
            display: 'flex',
            overflowX: 'auto',
            gap: '2rem',
            paddingBottom: '2rem',
            marginLeft: '-1rem',
            paddingLeft: '1rem',
            scrollSnapType: 'x mandatory'
          }}>
            {featuredArticles.map((article) => (
              <article 
                key={article.id}
                className="skeuomorphic-card"
                style={{
                  flexShrink: 0,
                  width: '20rem',
                  scrollSnapAlign: 'center',
                  borderRadius: '0.75rem',
                  overflow: 'hidden',
                  transition: 'transform 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-0.5rem)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <img 
                  alt={article.title}
                  style={{ width: '100%', height: '12rem', objectFit: 'cover' }}
                  src={article.image_url}
                />
                <div style={{ padding: '1.5rem' }}>
                  <h3 style={{ 
                    fontSize: '1.25rem', 
                    fontWeight: 'bold', 
                    marginBottom: '0.5rem',
                    color: '#f5f0e8'
                  }}>
                    {article.title}
                  </h3>
                  <p style={{ 
                    fontSize: '0.875rem', 
                    color: '#c0a080',
                    marginBottom: '1rem'
                  }}>
                    {article.excerpt}
                  </p>
                  <Link 
                    to={`/article/${article.slug}`}
                    style={{
                      color: '#c0a080',
                      textDecoration: 'none',
                      fontSize: '0.875rem',
                      fontWeight: '500'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                    onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                  >
                    Read More →
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Historical Periods */}
        <section style={{ marginBottom: '5rem' }}>
          <h2 style={{
            fontSize: '2.25rem',
            fontWeight: 'bold',
            marginBottom: '2rem',
            textAlign: 'center',
            color: '#c0a080',
            letterSpacing: '0.05em',
            fontFamily: 'Merriweather, serif'
          }}>
            Historical Periods
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem'
          }}>
            {periods.map((period) => (
              <Link
                key={period.id}
                to={`/period/${period.slug}`}
                className="skeuomorphic-card"
                style={{
                  display: 'block',
                  borderRadius: '0.75rem',
                  overflow: 'hidden',
                  textDecoration: 'none',
                  color: 'inherit',
                  transition: 'transform 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  const img = e.currentTarget.querySelector('img');
                  if (img) img.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  const img = e.currentTarget.querySelector('img');
                  if (img) img.style.transform = 'scale(1.1)';
                }}
              >
                <div style={{
                  height: '14rem',
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  <img 
                    alt={period.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transform: 'scale(1.1)', // Initial zoom
                      transition: 'transform 0.3s ease',
                      transformOrigin: 'center center'
                    }}
                    src={period.image_url}
                  />
                </div>
                <div style={{ padding: '1.5rem' }}>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: 'bold',
                    marginBottom: '0.5rem',
                    color: '#f5f0e8',
                    fontFamily: 'Merriweather, serif'
                  }}>
                    {period.name}
                  </h3>
                  <p style={{
                    fontSize: '0.875rem',
                    color: '#c0a080',
                    lineHeight: '1.4'
                  }}>
                    {period.description}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Popular Categories */}
        <section>
          <h2 className="text-4xl font-bold mb-8 text-center text-[var(--primary-color)] tracking-wider">
            Popular Categories
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
            {categories.map((category) => (
              <Link
                key={category.id}
                className="flex flex-col items-center gap-3 text-center p-4 skeuomorphic-card rounded-lg hover:bg-[var(--background-color)] transition-colors"
                to={`/category/${category.slug}`}
              >
                <span className="material-symbols-outlined text-4xl text-[var(--primary-color)]">
                  {category.icon}
                </span>
                <h3 className="font-bold">{category.name}</h3>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </>
  );
};

export default Home;